import { NavLink } from 'react-router-dom'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import clsx from 'clsx'

interface VersionInfo {
  version: string
  buildTime: string
}

// Frontend version from build time
const FRONTEND_VERSION = __APP_VERSION__
const FRONTEND_BUILD_TIME = __BUILD_TIME__

// Get the base API URL (without /api/v1 suffix)
const getBaseApiUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1'
  return apiUrl.replace(/\/api\/v1$/, '')
}

interface NavItem {
  name: string
  path: string
  icon: string
  roles?: string[]
}

const navItems: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: 'fa-chart-line' },
  { name: 'Doctors', path: '/doctors', icon: 'fa-user-md' },
  { name: 'Catalog', path: '/catalog', icon: 'fa-clipboard-list' },
  { name: 'Work Orders', path: '/work-orders', icon: 'fa-file-medical' },
  { name: 'Invoices', path: '/invoices', icon: 'fa-file-invoice-dollar' },
  { name: 'Payments', path: '/payments', icon: 'fa-credit-card' },
  { name: 'Statements', path: '/statements', icon: 'fa-chart-bar' },
  { name: 'Reports', path: '/reports', icon: 'fa-chart-pie' },
  { name: 'Users', path: '/users', icon: 'fa-user-shield', roles: ['admin'] },
  { name: 'Settings', path: '/settings', icon: 'fa-cog' },
  { name: 'Audit Log', path: '/audit', icon: 'fa-history', roles: ['admin'] },
]

export default function Sidebar() {
  const { sidebarOpen, sidebarCollapsed, toggleSidebarCollapse, setSidebarOpen } = useUIStore()
  const { user } = useAuthStore()
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null)

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const baseUrl = getBaseApiUrl()
        const response = await axios.get<VersionInfo>(`${baseUrl}/version?_t=${Date.now()}`)
        setVersionInfo(response.data)
      } catch {
        // Silently fail - version display is optional
      }
    }
    fetchVersion()
  }, [])

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles) return true
    return item.roles.includes(user?.role || '')
  })

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={clsx(
          'fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-30 transition-all duration-300',
          'hidden lg:block',
          sidebarCollapsed ? 'w-20' : 'w-64'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Nav Items */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-3">
              {filteredNavItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )
                    }
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <i className={`fas ${item.icon} w-5 text-center`}></i>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Version & Collapse Toggle */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            {/* Version Display - Always show frontend version, optionally show backend */}
            <div 
              className={clsx(
                "text-xs text-gray-400 dark:text-gray-500 text-center",
                sidebarCollapsed ? "px-1" : "px-3"
              )}
              title={`Frontend: v${FRONTEND_VERSION} (${new Date(FRONTEND_BUILD_TIME).toLocaleString()})${versionInfo ? `\nBackend: v${versionInfo.version}` : ''}`}
            >
              {sidebarCollapsed 
                ? `v${FRONTEND_VERSION}` 
                : `v${FRONTEND_VERSION}${versionInfo && versionInfo.version !== FRONTEND_VERSION ? ` / API v${versionInfo.version}` : ''}`
              }
            </div>
            <button
              onClick={toggleSidebarCollapse}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <i className={`fas fa-chevron-${sidebarCollapsed ? 'right' : 'left'}`}></i>
              {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={clsx(
          'fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-40 transform transition-transform duration-300 lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <nav className="py-4 overflow-y-auto h-full">
          <ul className="space-y-1 px-3">
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )
                  }
                >
                  <i className={`fas ${item.icon} w-5 text-center`}></i>
                  <span>{item.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  )
}
